/**
 * Vistas de previsualizacion, una por pestana del formulario.
 *
 * Cada una monta LOS MISMOS componentes y estilos que el sitio publico, para
 * que el editor vea el resultado al lado de lo que esta editando y no al final
 * en una pestana aparte.
 *
 * DE DONDE SALEN LOS VALORES. Los contenedores, alturas y estilos estan
 * copiados verbatim de flow y anotados con su archivo de origen. Los tests de
 * `cuerpoBloques.test.js` los leen DESDE flow y fallan si dejan de coincidir:
 * sin eso la previsualizacion se desalinea en silencio, que es lo que ya paso
 * una vez (alto 420 donde el sitio usa 520, gap 40 donde usa 56).
 *
 * `VistaCaso` es la excepcion parcial y conviene saberlo: el caso B2B lo dibuja
 * `BusinessCasos.tsx`, que depende de Next y del alias del repo, asi que no se
 * puede sincronizar como los otros seis componentes. Sus piezas se replican con
 * los estilos exactos del original, y las tres reglas `.epm-*` que necesita
 * estan abajo con su linea de origen.
 */
import React from 'react';

import { EditorialSplit } from '../../components/historia/EditorialSplit';
import { renderHighlighted } from '../../components/historia/highlight';
import { FeaturedStory } from '../../components/historia/FeaturedStory';
import { ProductBand } from '../../components/historia/ProductBand';
import { StoryCard } from '../../components/historia/StoryCard';
import Lienzo, { ANCHO_SITIO } from './Lienzo';

const DS = () =>
  (typeof window !== 'undefined' && window.DesignSystemRemix_1a4bd3) || {};

/** Los 16 FancyColor validos; uno fuera de la lista se descarta y la cita se
    dibuja sin resaltado, igual que hace `props.ts` en flow. */
const COLORES_VALIDOS = [
  'cyan', 'blue', 'sky', 'violet', 'indigo', 'purple', 'pink', 'red',
  'orange', 'lime', 'teal', 'slate', 'dark-teal', 'dark-cyan',
  'dark-indigo', 'dark-yellow',
];

/* Contenedores de /historias/[slug] — historia-editorial.css de flow. */
export const SECCION_HOME = { maxWidth: ANCHO_SITIO, margin: '0 auto', padding: '8px 20px 72px' };
export const SECCION_HERO = { maxWidth: ANCHO_SITIO, margin: '0 auto', padding: '16px 20px 8px' };
export const ARTICULO = { maxWidth: 1100, margin: '0 auto', padding: '36px 24px 8px' };
export const GRILLA_ARTICULO = { display: 'grid', gap: 56, alignItems: 'start' };
export const BANDA = { maxWidth: ANCHO_SITIO, margin: '0 auto', padding: '16px 20px' };

/* Contenedores de /business — empresas-pro-max.css de flow, lineas 18, 20 y 30. */
export const EPM_SPLIT = {
  display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, alignItems: 'stretch',
};
export const EPM_2UP = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 };
export const EPM_CARD = {
  background: 'var(--white, #fff)',
  border: '1px solid var(--yool-border, #E0E0E0)',
  borderRadius: 16,
};

/** Alturas del template, las mismas de `props.ts` en flow. */
export const ALTO_HERO = 520;
export const ALTO_MEDIO = 380;
export const ALTO_BANDA = 460;

export const texto = (v) => (v && String(v).trim() ? v : undefined);
export const color = (v) => (v && COLORES_VALIDOS.includes(v) ? v : undefined);

/**
 * Los terminos a resaltar, tal como los recibe el bloque en el sitio.
 *
 * Es el equivalente de `resaltado()` en flow/src/lib/historias/props.ts: filtra
 * los terminos en blanco y descarta un color que el DS no conoce, para que ese
 * termino caiga al color del bloque en vez de dibujarse sin color. Sin esto la
 * previsualizacion mostraria un resaltado que la pagina publicada no hace.
 */
export const resaltado = (v) => {
  if (typeof v === 'string') return texto(v);
  if (!Array.isArray(v)) return undefined;
  const terminos = v
    .filter((t) => t && typeof t.texto === 'string' && t.texto.trim())
    .map((t) => ({ texto: t.texto.trim(), color: color(t.color) }));
  return terminos.length ? terminos : undefined;
};

/** Aviso de campo faltante: dice el campo Y donde se llena. */
export function Falta({ children }) {
  const { YoolEmptyState } = DS();
  if (YoolEmptyState) {
    return <YoolEmptyState size="sm" title="Falta contenido" text={children} />;
  }
  return (
    <p
      style={{
        margin: 0, padding: '18px 20px', borderRadius: 12,
        border: '1px dashed var(--yool-border, #E0E0E0)',
        background: 'var(--yool-page, #F7F7F7)',
        fontSize: 13, color: 'var(--yool-gray1, #54585A)',
      }}
    >
      {children}
    </p>
  );
}

export function Rotulo({ children, nota }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <span
        style={{
          display: 'block', fontSize: 10, fontWeight: 600,
          letterSpacing: '.14em', textTransform: 'uppercase',
          color: 'var(--yool-muted, #8D8D8D)',
        }}
      >
        {children}
      </span>
      {nota ? (
        <span
          style={{
            display: 'block', marginTop: 4, fontSize: 12,
            color: 'var(--yool-muted, #8D8D8D)', textWrap: 'pretty',
          }}
        >
          {nota}
        </span>
      ) : null}
    </div>
  );
}

/** Props del hero: iguales para la home y para la pagina de la historia. */
export function propsHero(form) {
  return {
    eyebrow: texto(form.eyebrow),
    quote: form.quote,
    highlight: resaltado(form.highlight),
    highlightColor: color(form.highlight_color) || 'dark-cyan',
    text: texto(form.dek),
    image: form.imagen_url,
    imagePosition: texto(form.imagen_posicion),
    align: 'left',
  };
}

/* ── Portada ───────────────────────────────────────────────────────────── */

export function VistaPortada({ form }) {
  const hay = Boolean(texto(form.quote) && texto(form.imagen_url));
  if (!hay) {
    return (
      <Falta>
        {!texto(form.quote) && !texto(form.imagen_url)
          ? 'Falta la cita y la imagen para ver la portada.'
          : !texto(form.quote)
            ? 'Falta la cita para ver la portada.'
            : 'Falta la imagen para ver la portada.'}
      </Falta>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 24 }}>
      <div>
        <Rotulo nota="Como historia destacada en la home, y como hero de su propia página.">
          Destacada
        </Rotulo>
        <Lienzo>
          <div style={SECCION_HERO}>
            <FeaturedStory
              {...propsHero(form)}
              height={ALTO_HERO}
              primaryLabel="Leer historia"
              secondaryLabel="Conocer solución empresa"
            />
          </div>
        </Lienzo>
      </div>

      <div>
        <Rotulo nota="Y como tarjeta, cuando otra historia es la destacada. Se muestra en su celda real de la grilla de tres.">
          Tarjeta
        </Rotulo>
        <Lienzo>
          <div style={SECCION_HOME}>
            {/* Tres columnas, las mismas que la home: la sección arma filas de
                a 3 (flow/src/components/landing-b/LandingHistorias.tsx). */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
              <StoryCard
                image={form.imagen_url}
                imagePosition={texto(form.imagen_posicion)}
                title={form.quote || form.nombre}
                /* El resaltado va también en la tarjeta: es la misma cita de la
                   portada. Sin esto la previsualización mostraba la tarjeta sin
                   color, distinto de lo que publica el sitio. */
                highlight={resaltado(form.highlight)}
                highlightColor={color(form.highlight_color) || 'dark-cyan'}
                subtitle={[form.categoria, texto(form.tiempo_lectura)].filter(Boolean).join(' · ')}
              />
              <div /><div />
            </div>
          </div>
        </Lienzo>
      </div>
    </div>
  );
}

/* ── Cuerpo ────────────────────────────────────────────────────────────── */

function FichaStats({ stats }) {
  return (
    <>
      <h4
        style={{
          margin: '0 0 14px', font: '600 11px/1 var(--font-sans)',
          letterSpacing: '.08em', textTransform: 'uppercase',
          color: 'var(--yool-muted, #8D8D8D)',
        }}
      >
        El caso en números
      </h4>
      {stats.map((s) => (
        <div
          key={s.k}
          style={{
            display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
            gap: 12, padding: '9px 0',
            borderBottom: '1px solid var(--yool-border, #E0E0E0)',
          }}
        >
          <span style={{ font: '400 13px/1.4 var(--font-sans)', color: 'var(--yool-gray1, #54585A)' }}>
            {s.k}
          </span>
          <span
            style={{
              font: '700 15px/1.2 var(--font-sans)', color: 'var(--yool-black, #1A1A1A)',
              textAlign: 'right', fontVariantNumeric: 'tabular-nums',
            }}
          >
            {s.v}
          </span>
        </div>
      ))}
    </>
  );
}

export function VistaCuerpo({ form }) {
  const { YoolCard } = DS();
  const bloques = (form.cuerpo || []).filter((b) => texto(b.texto));
  const stats = (form.stats || []).filter((s) => texto(s.k) && texto(s.v));

  if (bloques.length === 0 && stats.length === 0) {
    return <Falta>Ningún bloque tiene texto todavía. Escribe uno para ver cómo se lee.</Falta>;
  }

  return (
    <Lienzo>
      <div style={ARTICULO}>
        <div
          style={{
            ...GRILLA_ARTICULO,
            gridTemplateColumns: stats.length > 0 ? '1.2fr 0.8fr' : '1fr',
          }}
        >
          <div>
            {bloques.length === 0 ? (
              <Falta>El cuerpo no tiene ningún bloque con texto.</Falta>
            ) : (
              bloques.map((b, i) => {
                if (b.tipo === 'subtitulo') {
                  return (
                    <h3
                      key={i}
                      style={{
                        margin: '36px 0 14px', font: '700 24px/1.2 var(--font-sans)',
                        letterSpacing: '-0.02em', color: 'var(--yool-black, #1A1A1A)',
                      }}
                    >
                      {b.texto}
                    </h3>
                  );
                }
                if (b.tipo === 'destacado') {
                  return (
                    <blockquote
                      key={i}
                      style={{
                        margin: '34px 0', paddingInlineStart: 20,
                        borderInlineStart: '3px solid var(--fy-cyan, #06B6D4)',
                        font: '600 22px/1.4 var(--font-sans)',
                        letterSpacing: '-0.01em', color: 'var(--yool-black, #1A1A1A)',
                      }}
                    >
                      {b.texto}
                    </blockquote>
                  );
                }
                return (
                  <p
                    key={i}
                    style={{
                      margin: '0 0 22px', font: '400 18px/1.75 var(--font-sans)',
                      color: 'var(--yool-gray1, #54585A)', textWrap: 'pretty',
                    }}
                  >
                    {b.texto}
                  </p>
                );
              })
            )}
          </div>

          {stats.length > 0 ? (
            <aside>
              {YoolCard ? (
                <YoolCard radius="lg" pad={22}>
                  <FichaStats stats={stats} />
                </YoolCard>
              ) : (
                <div
                  style={{
                    background: '#fff', borderRadius: 16, padding: '22px 24px',
                    border: '1px solid var(--yool-border, #E0E0E0)',
                  }}
                >
                  <FichaStats stats={stats} />
                </div>
              )}
            </aside>
          ) : null}
        </div>
      </div>
    </Lienzo>
  );
}

/* ── Editorial intermedio ──────────────────────────────────────────────── */

export function VistaMedio({ form }) {
  if (!texto(form.medio?.titulo)) {
    return (
      <Falta>
        Sin título, el bloque no se dibuja en la página. Es opcional: dejarlo vacío es
        una decisión válida.
      </Falta>
    );
  }

  return (
    <Lienzo>
      <div style={BANDA}>
        <EditorialSplit
          eyebrow={texto(form.medio.eyebrow)}
          title={form.medio.titulo}
          highlight={resaltado(form.medio.highlight)}
          highlightColor={color(form.medio.highlight_color) || 'blue'}
          text={texto(form.medio.texto)}
          image={texto(form.medio.imagen_url)}
          imagePosition={texto(form.medio.imagen_posicion)}
          /* `left`, el lado que publica el sitio (`propsMedio` en
             flow/src/lib/historias/props.ts). Estaba en `right`, así que la
             foto salía del lado contrario al que se ve en el editor.

             OJO con `imagePosition`: hoy flow lo IGNORA para este bloque —
             `propsMedio` pasa el literal 'center' y el tipo `MedioEditorial` ni
             siquiera declara `imagen_posicion`. Acá se pasa el valor real
             porque es lo que el bloque debería recibir; hasta que flow se
             arregle, la previsualización muestra un encuadre que la página no
             hace. A diferencia del hero y de la banda, en este bloque el texto
             va AL LADO de la foto y no encima, así que el encuadre no decide
             qué parte tapa el título. */
          imageSide="left"
          height={ALTO_MEDIO}
        />
      </div>
    </Lienzo>
  );
}

/* ── Caso B2B ──────────────────────────────────────────────────────────── */

/** Uno de los dos cuadros del panel navy. `resaltado` marca el "Después", que
    en el sitio lleva borde y rotulo cyan. Estilos verbatim de BusinessCasos. */
function CuadroCaso({ rotulo, valor, resaltado }) {
  return (
    <div
      style={{
        flex: 1, minWidth: 150, background: '#0b1c30',
        border: resaltado ? '1px solid rgba(34,211,238,.4)' : '1px solid rgba(255,255,255,.14)',
        borderRadius: 12, padding: '14px 16px',
      }}
    >
      <span
        style={{
          display: 'block', font: '700 11px/1 var(--font-sans)',
          letterSpacing: '.08em', textTransform: 'uppercase',
          color: resaltado ? 'var(--fy-dark-cyan, #22D3EE)' : 'rgba(255,255,255,.55)',
          marginBottom: 7,
        }}
      >
        {rotulo}
      </span>
      <span style={{ font: '500 14px/1.4 var(--font-sans)', color: 'rgba(255,255,255,.9)' }}>
        {valor}
      </span>
    </div>
  );
}

/** Un bloque de la tarjeta de apoyo. `destacado` es el Resultado, en cyan. */
function BloqueCaso({ rotulo, valor, destacado }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <span
        style={{
          display: 'block', font: '700 12px/1.4 var(--font-sans)',
          color: 'var(--yool-muted, #8D8D8D)', marginBottom: 4,
        }}
      >
        {rotulo}
      </span>
      <span
        style={{
          font: '400 13px/1.5 var(--font-sans)',
          color: destacado ? 'var(--fy-cyan, #06B6D4)' : 'var(--yool-gray1, #54585A)',
        }}
      >
        {valor}
      </span>
    </div>
  );
}

export function VistaCaso({ form }) {
  const caso = form.caso || {};
  const antes = texto(caso.antes);
  const despues = texto(caso.despues);
  const problema = texto(caso.problema);
  const solucion = texto(caso.solucion);
  const resultado = texto(caso.resultado);

  const hayPanel = Boolean(antes || despues);
  const hayTarjeta = Boolean(problema || solucion || resultado);

  if (!hayPanel && !hayTarjeta) {
    return (
      <Falta>
        Sin ninguno de los cinco campos, la historia no aparece como caso en /business.
        Es opcional.
      </Falta>
    );
  }

  if (form.categoria !== 'B2B') {
    return (
      <Falta>
        Esta historia es {form.categoria}: el caso solo se dibuja en /business, que
        muestra las B2B. Cambia la categoría en la pestaña Historia para verlo publicado.
      </Falta>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 24 }}>
      {hayPanel ? (
        <div>
          <Rotulo nota="Panel del caso destacado de /business, cuando esta historia es la primera B2B.">
            Caso destacado
          </Rotulo>
          <Lienzo>
            <div style={BANDA}>
              <div style={{ ...EPM_SPLIT, borderRadius: 20, overflow: 'hidden' }}>
                <div style={{ position: 'relative', minHeight: 360, background: '#121212' }}>
                  {texto(form.imagen_url) ? (
                    <img
                      src={form.imagen_url}
                      alt=""
                      style={{
                        position: 'absolute', inset: 0, width: '100%', height: '100%',
                        objectFit: 'cover',
                        objectPosition: texto(form.imagen_posicion) || 'center 30%',
                      }}
                    />
                  ) : null}
                </div>
                <div
                  style={{
                    background: 'var(--yool-navy, #0F2A44)', padding: 44,
                    display: 'flex', flexDirection: 'column', justifyContent: 'center',
                  }}
                >
                  {texto(form.eyebrow) ? (
                    <span
                      style={{
                        font: '700 11px/1 var(--font-sans)', letterSpacing: '.08em',
                        textTransform: 'uppercase', color: 'rgba(255,255,255,.55)',
                      }}
                    >
                      {form.eyebrow}
                    </span>
                  ) : null}
                  <h3
                    style={{
                      margin: '14px 0 0',
                      font: '700 clamp(26px,2.6vw,38px)/1.08 var(--font-sans)',
                      letterSpacing: '-0.035em', color: '#fff',
                    }}
                  >
                    {form.quote
                      ? renderHighlighted(form.quote, resaltado(form.highlight), 'dark-cyan')
                      : '(sin cita)'}
                  </h3>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 24 }}>
                    {antes ? <CuadroCaso rotulo="Antes" valor={antes} /> : null}
                    {despues ? <CuadroCaso rotulo="Después" valor={despues} resaltado /> : null}
                  </div>
                </div>
              </div>
            </div>
          </Lienzo>
        </div>
      ) : null}

      {hayTarjeta ? (
        <div>
          <Rotulo nota="Tarjeta de apoyo, cuando otra historia B2B es la destacada.">
            Tarjeta de apoyo
          </Rotulo>
          <Lienzo>
            <div style={BANDA}>
              <div style={EPM_2UP}>
                <div style={{ ...EPM_CARD, padding: 32 }}>
                  {texto(form.eyebrow) ? (
                    <span
                      style={{
                        display: 'block', font: '700 11px/1 var(--font-sans)',
                        letterSpacing: '.08em', textTransform: 'uppercase',
                        color: 'var(--yool-muted, #8D8D8D)', marginBottom: 10,
                      }}
                    >
                      {form.eyebrow}
                    </span>
                  ) : null}
                  <p
                    style={{
                      margin: '0 0 18px', font: '600 19px/1.4 var(--font-sans)',
                      letterSpacing: '-0.02em', color: 'var(--yool-black, #1A1A1A)',
                    }}
                  >
                    {form.quote || '(sin cita)'}
                  </p>
                  {problema ? <BloqueCaso rotulo="Problema" valor={problema} /> : null}
                  {solucion ? <BloqueCaso rotulo="Solución" valor={solucion} /> : null}
                  {resultado ? <BloqueCaso rotulo="Resultado" valor={resultado} destacado /> : null}
                </div>
                <div />
              </div>
            </div>
          </Lienzo>
        </div>
      ) : null}
    </div>
  );
}

/* ── Productos ─────────────────────────────────────────────────────────── */

export function VistaProducto({ form, productos = [] }) {
  const principal = (form.productos || []).find((p) => p.principal);
  const elegido = principal ? productos.find((p) => p.id === principal.producto_id) : null;

  if (!elegido) {
    return (
      <Falta>
        Sin producto principal no hay banda ni botón de simular en la página. Marca uno
        arriba.
      </Falta>
    );
  }

  return (
    <Lienzo>
      <div style={BANDA}>
        {/* Los mismos dos CTA que el sitio: "Simular" abre el simulador en la
            propia página de la historia (flow, historia/SheetSimulacion) y
            "Conoce más" sale a la página del producto. Sin acción acá: en el
            back office no hay simulador que abrir. */}
        <ProductBand
          eyebrow={texto(form.producto_banda?.eyebrow)}
          title={texto(form.producto_banda?.titulo) || elegido.nombre}
          highlight={resaltado(form.producto_banda?.highlight)}
          highlightColor={color(form.producto_banda?.highlight_color)}
          subtitle={texto(form.producto_banda?.subtitulo)}
          image={texto(form.producto_banda?.imagen_url)}
          imagePosition={texto(form.producto_banda?.imagen_posicion)}
          size="standard"
          height={ALTO_BANDA}
          textPosition="bottom-left"
          primaryLabel={`Simular ${elegido.nombre}`}
          secondaryLabel="Conoce más"
        />
      </div>
    </Lienzo>
  );
}
